import { useEffect, useRef, useState } from "react";
import { Camera, Pencil, Square, Type, Undo, Trash2, Download, Save } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * ImageMarkup — canvas overlay tool for admin to annotate quote images
 * with camera positions, notes, boxes. Returns a marked-up PNG data URL via onSave.
 */
export default function ImageMarkup({ src, onSave }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [tool, setTool] = useState("camera"); // camera, pen, box, text
  const [strokes, setStrokes] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [textInput, setTextInput] = useState(""); // pending text tool value
  const [pendingTextPos, setPendingTextPos] = useState(null); // {x,y}

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { imgRef.current = img; setLoaded(true); redraw(strokes, null); };
    img.src = src;
  }, [src]);

  useEffect(() => { if (loaded) redraw(strokes, preview); }, [strokes, preview, loaded]);

  const redraw = (list, prev) => {
    const c = canvasRef.current;
    const img = imgRef.current;
    if (!c || !img) return;
    const maxW = 900;
    const scale = Math.min(1, maxW / img.naturalWidth);
    c.width = img.naturalWidth * scale;
    c.height = img.naturalHeight * scale;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, c.width, c.height);
    const draw = (op) => {
      ctx.strokeStyle = "#FFB703";
      ctx.fillStyle = "#FFB703";
      ctx.lineWidth = 3;
      ctx.font = "bold 16px Manrope, sans-serif";
      if (op.type === "camera") {
        drawCameraIcon(ctx, op.x, op.y, op.n);
      } else if (op.type === "pen") {
        ctx.beginPath();
        op.pts.forEach((p, i) => { if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y); });
        ctx.stroke();
      } else if (op.type === "box") {
        ctx.strokeRect(op.x, op.y, op.w, op.h);
      } else if (op.type === "text") {
        ctx.fillStyle = "rgba(0,0,0,0.7)";
        const w = ctx.measureText(op.text).width + 12;
        ctx.fillRect(op.x - 4, op.y - 16, w, 22);
        ctx.fillStyle = "#FFB703";
        ctx.fillText(op.text, op.x + 2, op.y);
      }
    };
    list.forEach(draw);
    if (prev) draw(prev);
  };

  const drawCameraIcon = (ctx, x, y, n) => {
    ctx.save();
    ctx.fillStyle = "#FFB703";
    ctx.strokeStyle = "#0A0A0A";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, 18, 0, Math.PI * 2);
    ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#0A0A0A";
    ctx.font = "bold 18px Manrope, sans-serif";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(String(n), x, y);
    ctx.restore();
  };

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const onDown = (e) => {
    const p = getPos(e);
    if (tool === "camera") {
      const n = strokes.filter(s => s.type === "camera").length + 1;
      setStrokes([...strokes, { type: "camera", x: p.x, y: p.y, n }]);
    } else if (tool === "text") {
      setPendingTextPos(p);
      setTextInput("");
    } else if (tool === "pen") {
      setDrawing(true);
      setPreview({ type: "pen", pts: [p] });
    } else if (tool === "box") {
      setDrawing(true);
      setPreview({ type: "box", x: p.x, y: p.y, w: 0, h: 0, _sx: p.x, _sy: p.y });
    }
  };
  const onMove = (e) => {
    if (!drawing || !preview) return;
    const p = getPos(e);
    if (preview.type === "pen") setPreview({ ...preview, pts: [...preview.pts, p] });
    else if (preview.type === "box") setPreview({ ...preview, x: Math.min(preview._sx, p.x), y: Math.min(preview._sy, p.y), w: Math.abs(p.x - preview._sx), h: Math.abs(p.y - preview._sy) });
  };
  const onUp = () => {
    if (drawing && preview) {
      setStrokes([...strokes, preview]);
      setPreview(null);
    }
    setDrawing(false);
  };

  const commitText = () => {
    if (!pendingTextPos || !textInput.trim()) { setPendingTextPos(null); setTextInput(""); return; }
    setStrokes([...strokes, { type: "text", x: pendingTextPos.x, y: pendingTextPos.y, text: textInput.trim() }]);
    setPendingTextPos(null); setTextInput("");
  };

  const undo = () => setStrokes(strokes.slice(0, -1));
  const clear = () => setStrokes([]);
  const save = () => onSave?.(canvasRef.current.toDataURL("image/jpeg", 0.88));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center p-2 rounded-lg bg-secondary border border-border">
        {[
          { k: "camera", i: Camera, label: "Camera pin" },
          { k: "pen", i: Pencil, label: "Draw" },
          { k: "box", i: Square, label: "Box" },
          { k: "text", i: Type, label: "Note" },
        ].map(t => (
          <Button key={t.k} size="sm" variant={tool === t.k ? "default" : "ghost"} onClick={() => setTool(t.k)} className={tool === t.k ? "bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" : ""} data-testid={`markup-tool-${t.k}`}>
            <t.i className="h-4 w-4 mr-1.5" />{t.label}
          </Button>
        ))}
        <div className="w-px h-6 bg-border" />
        <Button size="sm" variant="ghost" onClick={undo} disabled={!strokes.length} data-testid="markup-undo"><Undo className="h-4 w-4 mr-1.5" />Undo</Button>
        <Button size="sm" variant="ghost" onClick={clear} disabled={!strokes.length} data-testid="markup-clear"><Trash2 className="h-4 w-4 mr-1.5" />Clear</Button>
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={save} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="markup-save"><Save className="h-4 w-4 mr-1.5" />Save markup</Button>
        </div>
      </div>
      {pendingTextPos && (
        <div className="flex gap-2 items-center p-2 rounded-lg bg-eagle-gold/10 border border-eagle-gold/40">
          <span className="text-sm font-medium text-eagle-gold">Note text:</span>
          <input
            autoFocus
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commitText(); if (e.key === "Escape") { setPendingTextPos(null); setTextInput(""); } }}
            placeholder="e.g. Blind spot — extra camera here"
            className="flex-1 bg-transparent border-b border-eagle-gold outline-none text-sm px-2 py-1"
            data-testid="markup-text-input"
          />
          <Button size="sm" onClick={commitText} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="markup-text-add">Add</Button>
          <Button size="sm" variant="ghost" onClick={() => { setPendingTextPos(null); setTextInput(""); }}>Cancel</Button>
        </div>
      )}
      <div className="border border-border rounded-lg overflow-hidden bg-black flex items-center justify-center max-h-[70vh] overflow-y-auto">
        <canvas
          ref={canvasRef}
          className="max-w-full cursor-crosshair block"
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
          data-testid="markup-canvas"
        />
      </div>
      <div className="text-xs text-muted-foreground">Tip: use <b>Camera</b> to drop numbered pins, <b>Note</b> to add text labels like &ldquo;Blind spot&rdquo; or &ldquo;Existing wire&rdquo;.</div>
    </div>
  );
}
